<template>
  <div class="conversion-area md:grid-cols-2 container grid grid-cols-1 py-12" :class="{ 'items-center': isAdjusted }">
    <div class="relative">
      <ProductGallery v-if="product" ref="productGallery" v-bind="{ product, injectedVideo }" />

      <div
        class="fluid-xxs uppercase tracking-wide font-medium h-7 md:justify-start box-border -top-4 absolute z-10 flex justify-center leading-3"
        :class="{ 'top-5 left-5': isAdjusted }"
        data-test-id="cardHeader"
      >
        <span v-if="cardHeader" class="h-7 items-center px-4 py-2" :style="cardHeader.style">
          {{ cardHeader.text }}
        </span>
      </div>
    </div>

    <div class="md:p-8 md:mt-0 mt-12">
      <div class="flex flex-col items-start">
        <div class="flex items-center self-start">
          <ProductTypeBadge
            v-if="product.productType && !isAdjusted"
            :product-type="product.productType"
            text-mobile
            inline-with-name
            border-mobile
            padding-mobile
          />

          <ClimateNeutralBadge v-if="climateNeutral" :is-epp="climateEPP" :is-band="climateBands" class="ml-3" />
        </div>

        <h1 v-if="product.title" :class="[isAdjusted ? 'fluid-2xl leading-none' : 'uppercase my-4']">
          {{ product.title }}
        </h1>

        <p v-if="product.subtitle && product.subtitle !== 'null'" class="max-w-md mb-4">
          {{ product.subtitle }}
        </p>

        <RatingStars v-if="showRating" :product="product" />
      </div>

      <ProductPriceLine
        v-if="variants.length"
        class="my-4"
        :price="selectedVariant.price"
        :compare-price="selectedVariant.compareAtPrice"
        :unit-price="selectedVariant.unitPrice"
        :unit-price-measurement="selectedVariant.unitPriceMeasurement"
        :show-quantity="variants.length === 1"
        :show-savings="showSavingsLabelPDP"
      />

      <PaypalExpressHint
        v-if="showPaypalHint && hasPaypalMethod && selectedVariant"
        :amount="selectedVariant.price.amount"
      />

      <template v-if="!isAdjusted && hasProductOptions">
        <section class="my-4" aria-labelledby="product-options-heading">
          <div v-if="showSizeTable" class="mb-4">
            <button
              v-if="productReference && productReference.length > 0"
              type="button"
              class="text-sm underline underline-offset-2 hover:no-underline"
              @click="openSizeTable"
            >
              {{ sizeTableButtonText || $t('sizeChart.findMySize') }}
            </button>
          </div>

          <div class="space-y-3">
            <ProductOptions
              v-for="(option, index) in product.options"
              :key="'opt_' + index"
              v-bind="{
                option,
                selectedVariant,
                variants,
                swatches: product.optionSwatches,
                maxSwatches: 12,
                alwaysShow: product.options.length >= 2,
                showVariantPriceDifference,
              }"
              ref="productOptions"
              swatch-img-class="md:h-8 md:w-8 w-6 h-6"
              data-test-id="colorOptions"
              @clickOption="setOption"
            />
          </div>
        </section>
      </template>

      <div
        v-if="showSizeAttentionText && !isAdjusted && hasProductOptions"
        class="my-4 flex w-fit gap-3 rounded-lg bg-gray-50 p-3"
      >
        <InfoSolidIcon aria-hidden="true" class="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        <div class="text-xs">
          <p class="font-medium">{{ $t('product.sizeAttentionTitle') }}</p>
          <p class="mt-0.5 text-gray-600">{{ $t('product.sizeAttentionText') }}</p>
        </div>
      </div>

      <CtaButton
        v-if="availableForSale"
        class="block"
        data-testid="addToCart"
        :loading="isAddingToCart"
        @click.native="onClickAddToCart"
      >
        {{ isCourse ? $t('cart.bookNow') : $t('cart.addToCart') }}
      </CtaButton>

      <button v-else-if="!availableForSale" class="btn-pill-gray inline-block pointer-events-none">
        {{ $t('product.comingSoon') }}
      </button>

      <client-only>
        <!-- prevent Hydrationerror on SSR racecondition on providing store -->
        <div v-if="shippingEntryUri && !isAddOn && !isCourse" class="fluid-xs leading-none mt-6">
          <i18n-t path="cart.shippingCost" tag="p" class="text-copy">
            <template #link>
              <nuxt-link :to="$localeRootPath(shippingEntryUri)">{{ $t('cart.link') }}</nuxt-link>
            </template>
          </i18n-t>
        </div>
      </client-only>

      <SmallAddOnTeaser v-if="addOn && !isAdjusted" class="mt-6 max-w-md" :heading="promoHeading" :image="promoImage" />

      <div v-if="showBenefits && !isCourse && !isAddOn && benefitBlocks && benefitBlocks.length > 0">
        <BenefitSection class="mt-4" :benefits="benefitBlocks" :show-made-in-germany="product.isMadeInGermany" />
      </div>
    </div>

    <PreAssessmentModal
      v-if="hasPreAssessment && showPreAssessmentModal"
      :showing="showPreAssessmentModal"
      :info="agreementCategory[0]"
      @buy="addToCart"
      @close="showPreAssessmentModal = false"
    />

    <SizeTableModal
      v-if="showSizeTable && productReference.length > 0 && showSizeTableModal"
      :showing="showSizeTableModal"
      :productReference="productReference[0]"
      @close="showSizeTableModal = false"
    />
  </div>
</template>

<script>
import { addVariantToCart } from '@/utils/product';
import { useAddToCartFromUrl } from '@/composables/useAddToCartFromUrl';
import { buildEECProduct } from '@/utils/tracking';
import ProductGallery from '@/components/shop/products/ProductGallery';
import { useCartSettingsStore } from '@/stores/cart-settings';
import { useCartStore } from '@/stores/cart';
import { useGlobalBannerStore } from '@/stores/global-banner';
import { Bus } from '@/utils/bus';

import addons from '@/mixins/addons';

import BenefitSection from '@/components/shop/BenefitSection/BenefitSection.vue';
import ClimateNeutralBadge from '@/components/shop/products/ClimateNeutralBadge';
import InfoSolidIcon from '@/components/icons/InfoSolidIcon.vue';
import CtaButton from '@/components/common/CtaButton';
import MadeInGermanyIcon from '@/components/icons/MadeInGermanyIcon.vue';
import PaypalExpressHint from '@/components/PaypalExpressHint.vue';
import PreAssessmentModal from '~/components/common/blocks/PreAssessmentModal';
import ProductOptions from '@/components/shop/products/ProductOptions';
import ProductPriceLine from '~/components/shop/products/ProductPriceLine';
import ProductTypeBadge from '@/components/shop/products/ProductTypeBadge';
import RatingStars from '@/components/shop/products/RatingStars';
import SizeTableModal from '@/components/common/SizeTableModal';
import SmallAddOnTeaser from '@/components/common/SmallAddOnTeaser';
import Tooltip from '@/components/common/Tooltip.vue';
import * as Sentry from '@sentry/vue';

export default defineNuxtComponent({
  components: {
    BenefitSection,
    ClimateNeutralBadge,
    CtaButton,
    InfoSolidIcon,
    MadeInGermanyIcon,
    ProductGallery,
    PaypalExpressHint,
    PreAssessmentModal,
    ProductOptions,
    ProductPriceLine,
    ProductTypeBadge,
    RatingStars,
    SizeTableModal,
    SmallAddOnTeaser,
    Tooltip,
  },

  mixins: [addons],

  props: {
    product: {
      type: Object,
      default: () => null,
    },
    addOn: {
      type: Object,
      default: () => null,
    },
    agreementCategory: {
      type: Array,
      default: () => null,
    },
    isAdjusted: {
      type: Boolean,
      default: false,
    },
    injectedVideo: {
      type: Object,
      default: () => ({}),
    },
    productReference: {
      type: Array,
      default: () => [],
    },
    showBenefits: {
      type: Boolean,
      default: true,
    },
    showPaypalHint: {
      type: Boolean,
      default: true,
    },
    showRating: {
      type: Boolean,
      default: true,
    },
    showSizeAttentionText: {
      type: Boolean,
      default: false,
    },
    showSizeTable: {
      type: Boolean,
      default: false,
    },
    sizeTableButtonText: {
      type: String,
      default: '',
    },
    showVariantPriceDifference: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showPreAssessmentModal: false,
      state: {
        currentSlide: 0,
        isFirst: false,
        isLast: false,
      },
      breakpoints: [
        {
          minWindowWidth: 0,
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      ],
      selectedVariant: null,
      showImageGallery: false,
      isAddingToCart: false,
      showSizeTableModal: false,
    };
  },

  setup() {
    const cartSettingsStore = useCartSettingsStore();
    const cartStore = useCartStore();
    const globalBannerStore = useGlobalBannerStore();

    return {
      cartSettingsStore,
      cartStore,
      globalBannerStore,
    };
  },

  computed: {
    shippingEntryUri() {
      return this.cartSettingsStore.shippingEntryUri;
    },
    hasPaypalMethod() {
      return this.cartSettingsStore.hasPaypalMethod;
    },
    lineItems() {
      return this.cartStore.lineItems;
    },
    benefitBlocks() {
      return this.globalBannerStore.benefitBlocks;
    },
    showSavingsLabelPDP() {
      return this.globalBannerStore.showSavingsLabelPDP;
    },

    variants() {
      return this.product?.variants?.edges?.map((e) => e.node) || [];
    },

    productImages() {
      return this.product?.images?.edges?.map((el) => el.node).filter(Boolean) || [];
    },

    cardHeader() {
      if (this.product?.cardHeader?.active) {
        const header = this.product.cardHeader;
        return { text: header.text, style: { color: header.textColor, backgroundColor: header.backgroundColor } };
      }
      return null;
    },

    productDescription() {
      return this.product?.descriptionHtml?.replace(/<[^>]*>?/gm, '');
    },

    fullProductName() {
      return this.product?.productType + ' ' + this.product?.title + ' ' + this.selectedVariant?.title;
    },

    availableForSale() {
      return this.product.availableForSale && this.selectedVariant?.available;
    },

    promoHeading() {
      return this.product?.digitalAddOn?.promoHeading;
    },

    promoImage() {
      return this.convertImage(this.product?.digitalAddOn?.digitalAddOnMainImageFullPath);
    },

    climateNeutral() {
      return this.product?.flags?.climateNeutral || this.climateEPP || this.climateBands;
    },

    climateEPP() {
      return this.product?.flags?.climateEPP;
    },

    climateBands() {
      return this.product?.flags?.climateBands;
    },

    hasPreAssessment() {
      return this.agreementCategory?.length > 0;
    },

    isCourse() {
      return this.product?.digitalProductType?.digitalProductType === 'Online Course';
    },

    isAddOn() {
      return this.product?.digitalProductType?.digitalProductType === 'Digital Training';
    },

    hasProductOptions() {
      return this.product?.options && this.product.options.length > 0;
    },

    selectedVariantTitle() {
      return this.selectedVariant?.title || '';
    },
  },

  watch: {
    selectedVariant(newValue) {
      if (!newValue) return;
      this.changeSlide(newValue.image?.id);
      this.trackSelectedVariant();
      this.updateSkuQueryParam();

      Bus.emit('pdp-variant-changed', { variantId: newValue.id });
    },
  },

  created() {
    if (!this.product) {
      return;
    }

    const selectedSku = this.$route.query.sku;

    if (selectedSku) {
      this.selectedVariant = this.variants.find((variant) => {
        return variant.sku?.toLowerCase() === selectedSku.toLowerCase();
      });
    }

    // if no variant matched the sku from query, select first available
    if (!this.selectedVariant) {
      this.selectedVariant = this.variants[0];
    }
  },

  mounted() {
    if (this.selectedVariant) {
      this.changeSlide(this.selectedVariant.image?.id);
    }

    // Handle query parameters for add to cart functionality
    const { handleAddToCartFromUrl } = useAddToCartFromUrl();
    handleAddToCartFromUrl({
      variants: this.variants,
      selectedVariant: this.selectedVariant,
      availableForSale: this.availableForSale,
      addToCart: this.addToCart,
      setVariant: (variant) => {
        this.selectedVariant = variant;
      },
    });
  },

  methods: {
    getProductPath() {
      const currentLocale = this.$route.path;
      return currentLocale;
    },

    setOption(newVariant) {
      this.selectedVariant = newVariant;
    },

    relatedVariant(imageId) {
      return (
        this.variants.find((variant) => {
          return variant.image?.id === imageId;
        }) || this.variants[0]
      );
    },

    trackSelectedVariant() {
      if (!this.selectedVariant) return;
      setTimeout(() => {
        this.$_gtm?.push({
          event: 'EEC_view_item',
          event_name: 'view_item',
          currencyCode: this.selectedVariant.price.currencyCode,
          ecommerce: {
            items: [
              buildEECProduct(
                this.selectedVariant,
                this.product,
                this.rating?.value,
                this.productImages[0]?.src || null
              ),
            ],
          },
        });
      }, 300);
    },

    changeSlide(imageId) {
      // only execute if gallery is mounted
      if (!this.$refs.productGallery) {
        return;
      }

      const position = this.productImages.findIndex((image) => {
        if (image) {
          return image.id === imageId;
        } else {
          return false;
        }
      });

      if (position >= 0 && this.$refs.productGallery) {
        // Call the slideTo method on the ProductGallery component
        return this.$refs.productGallery.slideTo(position);
      }

      return -1;
    },

    async onClickAddToCart() {
      if (this.hasPreAssessment) {
        this.showPreAssessmentModal = true;
      } else {
        this.isAddingToCart = true;

        try {
          await this.addToCart();
        } catch (e) {
          Sentry.captureException(e);
        } finally {
          this.isAddingToCart = false;
        }
      }
    },

    addToCart(quantity = 1) {
      this.clearAtcQueryParam();

      return addVariantToCart.call(
        this,
        this.product,
        this.selectedVariant,
        this.isCourse,
        this.rating?.value,
        this.addOn,
        quantity
      );
    },

    openSizeTable() {
      this.showSizeTableModal = true;
    },

    clearAtcQueryParam() {
      if (window && window.location && window.history) {
        const url = new URL(window.location.href);
        url.searchParams.delete('atc');

        window.history.replaceState({}, '', url.toString());
      }
    },

    updateSkuQueryParam() {
      if (window && window.location && window.history) {
        // Update URL without triggering navigation
        const url = new URL(window.location.href);
        url.searchParams.delete('sku');

        if (this.selectedVariant && this.selectedVariant.sku) {
          url.searchParams.set('sku', this.selectedVariant.sku);
        }

        window.history.replaceState({}, '', url.toString());
      }
    },
  },
});
</script>
